import { Mic, MicOff, PhoneOff } from 'lucide-react'
import { CompanionAvatar } from './CompanionAvatar'
import { CoachOrb } from './CoachOrb'
import { cn } from '@/lib/utils'
import type { Companion } from '../data/companions'
import type { Mood } from '@/lib/mood'
import type { CallPhase } from './AvatarCallScreen'

const PHASE_LABEL: Record<CallPhase, string> = {
  connecting: 'Connecting…',
  listening: 'Listening…',
  thinking: 'Thinking…',
  speaking: 'Speaking…',
  error: "Couldn't connect",
}

// Voice-only call — no video/LiveKit, just the living avatar + spoken audio (existing TTS path).
// Same hands-free conversation loop and call-screen chrome as AvatarCallScreen, at a fraction of
// the cost since there's no talking-head video session involved.
export function VoiceCallScreen({
  coach, phase, mood = 'neutral', transcript, muted, onToggleMute, onEndCall,
}: {
  coach: Companion
  phase: CallPhase
  mood?: Mood
  transcript: string
  muted: boolean
  onToggleMute: () => void
  onEndCall: () => void
}) {
  return (
    <div className="relative flex flex-1 min-h-0 flex-col items-center justify-between bg-black px-4 py-6">
      {/* Top bar */}
      <div className="flex w-full items-center justify-between">
        <div className="text-white">
          <div className="text-sm font-semibold drop-shadow">{coach.name}</div>
          <div className="text-[11px] text-white/80 drop-shadow">{coach.tagline}</div>
        </div>
        <button
          onClick={onEndCall}
          className="flex size-9 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur-sm hover:bg-white/25"
          aria-label="End voice call"
        >
          <PhoneOff className="size-4" />
        </button>
      </div>

      {/* Center: living avatar, big */}
      <div className="flex flex-1 items-center justify-center">
        <CoachOrb active={phase === 'speaking' || phase === 'listening'} color={coach.accent}>
          <CompanionAvatar c={coach} size={168} speaking={phase === 'speaking'} mood={mood} />
        </CoachOrb>
      </div>

      {/* Bottom: status + captions + mic control */}
      <div className="flex w-full flex-col items-center gap-3">
        <div className="flex items-center gap-2">
          {phase === 'listening' && <span className="relative flex size-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-75" /><span className="relative inline-flex size-2 rounded-full bg-success" /></span>}
          <span className="text-xs font-medium text-white/90 drop-shadow">{muted && phase === 'listening' ? 'Mic muted' : PHASE_LABEL[phase]}</span>
        </div>
        {transcript && (
          <p className="max-w-sm text-center text-sm text-white drop-shadow-md line-clamp-2">{transcript}</p>
        )}
        <button
          onClick={onToggleMute}
          className={cn(
            'flex size-12 items-center justify-center rounded-full backdrop-blur-sm transition-colors',
            muted ? 'bg-alert text-white' : 'bg-white/15 text-white hover:bg-white/25',
          )}
          aria-label={muted ? 'Unmute microphone' : 'Mute microphone'}
        >
          {muted ? <MicOff className="size-5" /> : <Mic className="size-5" />}
        </button>
      </div>
    </div>
  )
}
